import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Trash2 } from "lucide-react";

export default function Templates() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", name: "", category: "", subject: "", body_text: "" });

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [t, c] = await Promise.all([api.get("/templates", { params }), api.get("/companies")]);
    setItems(t.data); setCompanies(c.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/templates", { ...form, body_html: form.body_text.replace(/\n/g, "<br>") });
      toast.success("Template saved");
      setOpen(false);
      setForm({ ...form, name: "", category: "", subject: "", body_text: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="templates-page">
      <PageHeader
        title="Email templates"
        subtitle="Reusable snippets for common workflows — acknowledgements, invoices, escalations."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-template-button"><Plus className="h-4 w-4 mr-1.5" /> New template</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Create template</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <F label="Company">
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="tpl-company-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="tpl-name-input" /></F>
                  <F label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="tpl-category-input" /></F>
                </div>
                <F label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="tpl-subject-input" /></F>
                <F label="Body"><Textarea rows={8} value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} required data-testid="tpl-body-input" /></F>
                <DialogFooter><Button type="submit" className="rounded-sm" data-testid="tpl-create-submit">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-8 grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((t) => (
          <div key={t.id} className="border border-border rounded-sm p-4 hover:bg-muted/30 transition-colors" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-terracotta" />
                  <span className="font-medium">{t.name}</span>
                </div>
                <div className="text-[11px] font-mono text-muted-foreground mt-1">{t.category || "uncategorized"}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(t.id)} data-testid={`delete-tpl-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="mt-3 text-sm font-medium">{t.subject}</div>
            <div className="mt-1 text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">{t.body_text}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-muted-foreground text-sm">No templates yet.</div>}
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
